import React, { useState, useEffect } from 'react';
import useAppStore, { useCalculationsSelector, EmployeeData } from '../store/appStore';
import PasswordPrompt from '../components/PasswordPrompt';
import { Page, Section, PageTitle } from '../components/UI';
import EditableField from '../components/EditableField';
import { isHj1ClientLocked } from '../utils/hjLock';
import Hj1MandantLockBanner from '../components/Hj1MandantLockBanner';
import { isSoloModeActive } from '../utils/soloMode';
import { calculateConvergedUnternehmerStundenlohn } from '../utils/unternehmerlohnConvergence';
import ItBzLohnberechnungInfoModal from '../components/personal/ItBzLohnberechnungInfoModal';

// --- HELPER COMPONENTS FOR DETAILS VIEW (UNTOUCHED LOGIC) ---
interface FormulaDisplayProps {
    show: boolean;
    value: number | string;
    formula: string;
    type?: 'currency' | 'percentage' | 'number' | 'text';
    decimalPlaces?: number;
    suffix?: string;
    highlightNegative?: boolean;
    isZielProzent?: boolean;
    zielProzentWert?: number;
    bgColor?: string;
    textColor?: string;
    isLocked?: boolean;
    ruecklagenZielFaktor?: number;
}

const FormulaDisplay: React.FC<FormulaDisplayProps> = ({ show, value, formula, type = 'currency', decimalPlaces = 0, suffix = '', highlightNegative = false, isZielProzent = false, zielProzentWert = 0, bgColor = 'bg-pe-tile-header dark:bg-slate-800', textColor = 'text-pe-accent-text', isLocked = false, ruecklagenZielFaktor = 0.10 }) => {
    const currencySymbol = useAppStore(state => state.settings.countries[state.settings.selectedCountryId]?.currency || 'EUR');

    const formatCurrencyInternal = (val: number, decimals = 0) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: currencySymbol, minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(val || 0);
    const formatNumberInternal = (val: number, decimals = 1, suff = '') => val.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + (suff ? ` ${suff}` : '');
    const formatPercentageInternal = (val: number, decimals = 1) => (val * 100).toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + '%';

    if (show) {
        return <div className="absolute inset-0 bg-stone-900 dark:bg-black bg-opacity-80 flex items-center justify-center p-1 rounded-md z-20 cursor-help"><p className="text-white text-[10px] text-center font-mono select-all leading-tight">{formula}</p></div>;
    }

    let displayValue;
    const numberValue = Number(value);

    if (type === 'text') {
        displayValue = value;
    } else if (isNaN(numberValue)) {
        displayValue = "-";
    } else if (type === 'currency') {
        displayValue = formatCurrencyInternal(numberValue, decimalPlaces);
    } else if (type === 'percentage') {
        displayValue = formatPercentageInternal(numberValue, decimalPlaces);
    } else {
        displayValue = formatNumberInternal(numberValue, decimalPlaces, suffix);
    }

    let finalTextColor = textColor;

    if (isZielProzent) {
        const prozentWert = zielProzentWert !== undefined ? zielProzentWert : numberValue;
        if (prozentWert < 0) {
            finalTextColor = 'text-red-500';
        } else if (prozentWert < ruecklagenZielFaktor) {
            finalTextColor = 'text-yellow-500';
        } else {
            finalTextColor = 'text-green-500';
        }
    } else if (highlightNegative) {
        if (numberValue < 0) {
            finalTextColor = 'text-red-500';
        }
    }

    const finalBgColor = isLocked ? 'bg-gray-500' : bgColor;
    if (isLocked) finalTextColor = 'text-pe-muted dark:text-gray-300';


    return (
        <div className={`w-full h-full p-2 rounded-md text-center ${finalBgColor} ${finalTextColor} flex items-center justify-center font-bold font-mono`}>
            {displayValue}
        </div>
    );
};

const ValueWithFormula = ({ children, formula }: { children: React.ReactNode, formula?: string }) => {
    const { showFormulas } = useAppStore();
    const wrappedChildren = React.Children.map(children, child => {
         if (React.isValidElement(child) && child.type === FormulaDisplay) {
             return React.cloneElement(child as React.ReactElement<FormulaDisplayProps>, { show: showFormulas });
         }
        return child;
    });

    return (
        <div className="relative h-full w-full">
            {wrappedChildren}
            {showFormulas && formula && (
                 <div className="absolute inset-0 bg-stone-900 dark:bg-black bg-opacity-80 flex items-center justify-center p-1 rounded-md z-20 cursor-help">
                     <p className="text-white text-[10px] text-center font-mono select-all leading-tight">{formula}</p>
                 </div>
            )}
        </div>
    );
};

const DataBox = ({ label, children, labelWidth = 'w-48', tooltip }: { label: string, children: React.ReactNode, labelWidth?: string, tooltip?: string }) => (
    <div className="flex items-center space-x-2 h-11">
        <div
            className={`flex-shrink-0 bg-pe-card text-pe-text text-sm font-semibold p-2 rounded-l-md ${labelWidth} text-left h-full flex items-center border-r border-pe-border`}
            data-tooltip-id="app-tooltip"
            data-tooltip-content={tooltip}
        >
            {label}
        </div>
        <div className="flex-grow h-full relative">{children}</div>
    </div>
);

const DisplayCard = ({ label, children, containerClassName = "w-36", headerExtra }: { label: React.ReactNode, children: React.ReactNode, containerClassName?: string, headerExtra?: React.ReactNode }) => (
    <div className={`bg-pe-card p-2 rounded-md text-center border border-pe-border ${containerClassName}`}>
         <div className="text-sm text-pe-text mb-1 flex items-center justify-center gap-2 flex-wrap">
            <span>{label}</span>
            {headerExtra}
         </div>
         <div className="min-h-[44px]"> 
             {children}
         </div>
     </div>
);

const HeaderBox = ({ title }: { title: string }) => ( <div className="bg-pe-card border border-pe-border text-pe-text font-bold text-center py-1 px-4 rounded-md mb-2">{title}</div> );

// --- DETAILS ANSICHT COMPONENT (PRESERVED) ---
    const DetailsAnsicht = ({ calc }: { calc: ReturnType<typeof useCalculationsSelector> }) => {
     const {
         formatCurrency, prodUnternehmerlohn, mwstSatz, sollumsatz, einkommenssteuerUnternehmer,
         erwarteteEinkommenssteuer, einkommenssteuerSatz,
         unproduktiveLohnkostenMitarbeiter, azubiKostenGesamt, gemeinkostenGesamtMonat,
         produktiveKostenGesamtMonat, unprodUnternehmerlohn, produktiveKostenTabelle, personalData,
         materialkostenMonat, geraetekostenMonat, bwaData, zeitraum, lohnfaktorFortzahlung, lohnfaktorAktiv, clientData,
         azubiAssistenzData, stundenlohnChef, planumsatzGesamtNettoKalkuliert, monatsumsatzNetto
     } = calc;
     const mwstSatzSafe = (mwstSatz == null || !Number.isFinite(Number(mwstSatz))) ? 19 : Number(mwstSatz);

    const ekSteuerAnzeige = Number(einkommenssteuerUnternehmer || erwarteteEinkommenssteuer) || 0;
    const ekSteuerSatzAnzeige = Number(einkommenssteuerSatz) || 0;
    const lfFuerPlan = Number(lohnfaktorAktiv) || Number(lohnfaktorFortzahlung) || 0;
    const planumsatzDetails = Number(planumsatzGesamtNettoKalkuliert) || 0;
    const { showFormulas } = useAppStore();

    const SectionHeader = ({ title }: { title: string }) => <div className="bg-pe-tile-header dark:bg-slate-700 text-pe-text font-bold text-center py-1 px-4 rounded-md my-3 border border-pe-border-strong dark:border-slate-600">{title}</div>;

    const CostRow = ({
        label,
        value,
        formula,
        hint,
        mutedZero = true,
    }: {
        label: string;
        value: number;
        formula?: string;
        hint?: string;
        mutedZero?: boolean;
    }) => {
        const isZero = !value || Math.abs(value) < 0.0001;
        return (
        <ValueWithFormula formula={formula}>
            <div className={`flex justify-between items-center py-1.5 px-3 bg-pe-tile-header dark:bg-slate-800 rounded mb-1 min-h-[40px] border border-pe-border dark:border-slate-700 ${isZero && mutedZero ? 'opacity-60' : ''}`}>
                <div className="flex flex-col pr-2 min-w-0">
                    <span className="text-sm text-pe-muted dark:text-gray-300">{label}</span>
                    {hint ? (
                        <span className="text-[10px] text-amber-400/90 leading-tight mt-0.5">{hint}</span>
                    ) : null}
                </div>
                <span className="px-3 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono font-semibold shrink-0">{formatCurrency(value, 0)}</span>
            </div>
        </ValueWithFormula>
        );
    };

    const getProzent = (wert: number) => monatsumsatzNetto > 0 ? `${((wert / monatsumsatzNetto) * 100).toFixed(1).replace('.', ',')}%` : '0,0%';

    const mitarbeiterDatenFuerPlanumsatz = (personalData || []).filter((ma: EmployeeData) => ma.taetigkeit !== 'produktiver Azubi' && ma.taetigkeit !== 'Azubi');

     const betriebskostenPostenFull = [
         { key: 'raumkosten', label: 'Raumkosten' }, { key: 'betriebssteuern', label: 'Betriebliche Steuern' },
         { key: 'versicherungen', label: 'Versicherungen/Beiträge' }, { key: 'besondereKosten', label: 'Besondere Kosten' },
         { key: 'kfzKosten', label: 'KFZ Kosten' }, { key: 'werbeReisekosten', label: 'Werbe/Reisekosten' },
         { key: 'warenabgabe', label: 'Warenabgabe an MA' }, { key: 'reparatur', label: 'Reparatur/Instandhaltung' },
         { key: 'sonstigeKosten', label: 'Sonstige Kosten' }, { key: 'kredite', label: 'Kredite, Zinsen u.ä.' },
         { key: 'zinsaufwendungen', label: 'Zinsaufwendungen' }, { key: 'gewerbesteuer', label: 'Gewerbesteuer' },
         { key: 'koerperschaftssteuer', label: 'Körperschaftssteuer (GmbH)' }, { key: 'budgetPlansumme', label: 'Budget Plansumme' },
         { key: 'preisExpert', label: 'PreisExpert Beratung' },
         { key: 'sonstigeEintrag1', label: bwaData.sonstigeEintrag1_label || 'Sonstige Einträge 1' },
         { key: 'sonstigeEintrag2', label: bwaData.sonstigeEintrag2_label || 'Sonstige Einträge 2' },
         { key: 'sonstigeEintrag3', label: bwaData.sonstigeEintrag3_label || 'Sonstige Einträge 3' },
         { key: 'sonstigeEintrag4', label: bwaData.sonstigeEintrag4_label || 'Sonstige Einträge 4' },
    ];
    const positiveWertePostenFull = [ { key: 'abschreibungen', label: 'Abschreibungen (-)'}, { key: 'zinsenMiete', label: 'Sonstige Einnahmen (-)'} ];
    const geraetePostenFull = Array.from({ length: 10 }, (_, i) => {
        const n = i + 1;
        const key = `geraetEintrag${n}`;
        return { key, label: (bwaData as any)[`${key}_label`] || `Gerät ${n}` };
    });

    const betriebskostenMonatSumme = betriebskostenPostenFull.reduce(
        (sum, posten) => sum + ((bwaData[posten.key] || 0) / (zeitraum || 1)),
        0
    );
    const positiveWerteMonatSumme = positiveWertePostenFull.reduce(
        (sum, posten) => sum + ((bwaData[posten.key] || 0) / (zeitraum || 1)),
        0
    );
    const geraetekostenMonatSumme = geraetePostenFull.reduce(
        (sum, posten) => sum + ((bwaData[posten.key] || 0) / (zeitraum || 1)),
        0
    );

    const ekSteuerHint =
        ekSteuerAnzeige <= 0
            ? ekSteuerSatzAnzeige <= 0
                ? 'Quelle: Unternehmerwerte → Persönlicher Steuersatz fehlt (0 %). Formel: (Entnahme + Sozial) × Steuersatz.'
                : `Steuersatz ${ekSteuerSatzAnzeige.toFixed(1).replace('.', ',')} % ist gesetzt, aber Entnahme+Sozial = 0 → Steuerbetrag 0.`
            : `Errechnet aus Unternehmerwerte: (Entnahme + Sozial) × ${ekSteuerSatzAnzeige.toFixed(1).replace('.', ',')} %.`;

     const summeStundenlohnTeam_details = mitarbeiterDatenFuerPlanumsatz.reduce((sum: number, ma: EmployeeData): number => sum + (ma.stundenlohnGesamt || 0), (stundenlohnChef || 0));
     const anzahlMitarbeiterProd_details = mitarbeiterDatenFuerPlanumsatz.length;
     const anzahlChefProd_details = prodUnternehmerlohn > 0 ? 1 : 0;
     const anzahlProduktivesTeamGesamt_details = anzahlMitarbeiterProd_details + anzahlChefProd_details;
     const durchschnittlicherStundenlohn_details = anzahlProduktivesTeamGesamt_details > 0 ? summeStundenlohnTeam_details / anzahlProduktivesTeamGesamt_details : 0;
     // Excel: Summe(Azubi-Std-Löhne) * 2/3 — nicht Ø * 2/3
     const avgStundenlohnAzubiProd_details = azubiAssistenzData.length > 0
         ? (azubiAssistenzData as EmployeeData[]).reduce((sum: number, ma: EmployeeData) => sum + (ma.stundenlohnGesamt || 0), 0 as number) * (2 / 3)
         : 0;
     const berechnungsstundenlohnSumme_details = summeStundenlohnTeam_details + avgStundenlohnAzubiProd_details;

    const geoeffneteStunden = clientData?.unternehmerData?.geoeffneteStunden || [];
    const summeStunden = geoeffneteStunden.reduce((sum: number, stunde: number): number => sum + (stunde || 0), 0 as number);
    const anzahlTageGeoeffnet = geoeffneteStunden.filter((stunde: number) => (stunde || 0) > 0).length;
    const durchschnittlicheOeffnungszeit = anzahlTageGeoeffnet > 0 ? summeStunden / anzahlTageGeoeffnet : 0;
    const durchschnittlicherUmsatzProTag = (durchschnittlicherStundenlohn_details * durchschnittlicheOeffnungszeit) * lfFuerPlan;

    return (
        <div className="bg-pe-tile-header dark:bg-gray-900 rounded-lg p-4 mt-8 w-full border border-pe-border dark:border-slate-700">
             <div id="nebenrechnungen_prod_lohnkosten">
                 <PageTitle>Produktive Lohnkosten (Nebenrechnung)</PageTitle>
                 
                  <div className="space-y-2 mt-4">
                        <div className="grid grid-cols-3 gap-4 text-sm font-semibold text-pe-muted dark:text-gray-400 px-4">
                            <div>Mitarbeiter</div>
                            <div className="text-center">Lohn inkl. Nebenkosten</div>
                            <div className="text-right">Planumsatz netto</div>
                        </div>
                        <div className="bg-pe-tile-header dark:bg-slate-800 rounded-lg p-2 space-y-1 border border-pe-border dark:border-slate-700">
                            {mitarbeiterDatenFuerPlanumsatz.map((ma: EmployeeData) => {
                               const ma_basis = (ma.grundgehalt || 0) + (ma.lohnzusatz || 0) + (ma.bonus || 0);
                               const ma_planumsatzNetto = (ma_basis * lfFuerPlan) / (1 + (mwstSatzSafe / 100));
                               return (
                                   <div key={ma.id} className="grid grid-cols-3 gap-4 p-2 rounded-md hover:bg-pe-tile-header dark:bg-slate-700 h-11 items-center border-b border-pe-border dark:border-slate-700 last:border-0">
                                            <div className="font-semibold text-pe-text">{ma.name}</div>
                                            <ValueWithFormula formula="Grundlohn*(1+NK%)+Bonus+Lohnz.">
                                                <FormulaDisplay show={showFormulas} formula="" value={ma.totalCost} type="currency" decimalPlaces={0} bgColor="bg-pe-sunken dark:bg-slate-900" />
                                            </ValueWithFormula>
                                            <ValueWithFormula formula={`(${ma_basis.toFixed(0)} * ${lfFuerPlan.toFixed(2)}) / ${1 + (mwstSatzSafe / 100)}`}>
                                                <FormulaDisplay show={showFormulas} formula="" value={ma_planumsatzNetto} type="currency" decimalPlaces={0} textColor="text-pe-text" bgColor="bg-pe-sunken dark:bg-slate-900"/>
                                            </ValueWithFormula>
                                   </div>
                               );
                            })}
                            <div className="grid grid-cols-3 gap-4 p-2 rounded-md hover:bg-pe-tile-header dark:bg-slate-700 border-t-2 border-pe-border-strong dark:border-slate-600 mt-2 pt-2 h-11 items-center">
                                <div className="font-semibold text-pe-text">prod. Unternehmerlohn</div>
                                <ValueWithFormula formula="Chef-Stundenlohn * Monatsstunden">
                                    <FormulaDisplay show={showFormulas} formula="" value={prodUnternehmerlohn} type="currency" decimalPlaces={0} bgColor="bg-pe-sunken dark:bg-slate-900" />
                                </ValueWithFormula>
                                <ValueWithFormula formula={`(${prodUnternehmerlohn.toFixed(0)} * ${lfFuerPlan.toFixed(2)}) / ${1 + (mwstSatzSafe / 100)}`}>
                                    <FormulaDisplay show={showFormulas} formula="" value={(prodUnternehmerlohn * lfFuerPlan) / (1 + (mwstSatzSafe / 100))} type="currency" decimalPlaces={0} textColor="text-pe-text" bgColor="bg-pe-sunken dark:bg-slate-900"/>
                                </ValueWithFormula>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm font-bold text-pe-text px-4 pt-4 h-11 items-center">
                            <div>prod. Lohnkosten</div>
                            <ValueWithFormula formula="Summe prod. Löhne (MA ohne Azubis + Chef)">
                                <FormulaDisplay show={showFormulas} formula="" value={produktiveKostenTabelle} type="currency" decimalPlaces={0} bgColor="bg-pe-tile-header dark:bg-slate-800" />
                            </ValueWithFormula>
                            <ValueWithFormula formula="Summe Planumsätze (MA ohne Azubis + Chef)">
                                <FormulaDisplay show={showFormulas} formula="" value={planumsatzDetails} type="currency" decimalPlaces={0} textColor="text-green-800 dark:text-white" bgColor="bg-green-100 dark:bg-green-900"/>
                            </ValueWithFormula>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm font-bold text-pe-text px-4 pt-2 h-11 items-center">
                            <div className="text-pe-muted dark:text-gray-400">Lohnfortzahlung {calc.settings.global.Urlaubsausgleich}%</div>
                            <ValueWithFormula formula={`prod. Lohnkosten (ohne Chef) * ${calc.settings.global.Urlaubsausgleich}%`}>
                                <FormulaDisplay show={showFormulas} formula="" value={calc.lohnfortzahlung || 0} type="currency" decimalPlaces={0} bgColor="bg-pe-tile-header dark:bg-slate-800" />
                            </ValueWithFormula>
                            <div className="text-right"></div>
                        </div>
                  </div>
             </div>

            <div id="kostenaufstellung" className="mt-8">
                 <SectionHeader title="Gemein- & unproduktive Lohnkosten (pro Monat Ø)" />
                 <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2">
                       <CostRow
                           label="EK-Steuer (Unternehmer)"
                           value={ekSteuerAnzeige}
                           formula="(Entnahme + Sozialkosten) × Steuersatz %"
                           hint={ekSteuerHint}
                       />
                       <CostRow label={`unpr. Azubis (${(1/3*100).toFixed(0)}%)`} value={azubiKostenGesamt * (1/3)} formula={`Gesamtkosten Azubis * ${(1/3*100).toFixed(0)}%`} />
                       <CostRow label="unpr. Mitarbeiter (Kosten inkl. Sozi)" value={unproduktiveLohnkostenMitarbeiter} formula="Summe Kosten unpr. MA" />
                 </div>

                 <SectionHeader title="Betriebskosten = netto p. Monat (detailliert, vollständig)" />
                 <p className="text-[10px] text-slate-500 mb-2 px-1">
                     Alle BWA-Posten der Gemeinkosten-Formel — auch mit 0 EUR. Nichts wird ausgeblendet.
                 </p>
                 <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2">
                       {betriebskostenPostenFull.map(posten => {
                           const wert = (bwaData[posten.key] || 0) / (zeitraum || 1);
                           return <CostRow key={posten.key} label={posten.label} value={wert} formula="BWA-Wert / Zeitraum" />;
                       })}
                       {positiveWertePostenFull.map(posten => {
                           const wert = (bwaData[posten.key] || 0) / (zeitraum || 1);
                           return <CostRow key={posten.key} label={posten.label} value={-wert} formula="BWA-Wert / Zeitraum (Abzug)" />;
                       })}
                       <div className="flex justify-between items-center py-2 px-3 mt-1 border-t border-pe-border-strong dark:border-slate-600 font-bold text-pe-text text-sm">
                           <span>Summe Betriebskosten − Positive Werte</span>
                           <span className="font-mono text-pe-accent-text">{formatCurrency(betriebskostenMonatSumme - positiveWerteMonatSumme, 0)}</span>
                       </div>
                 </div>
                  <SectionHeader title="Gerätekosten (pro Monat Ø, vollständig)" />
                  <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2">
                      {geraetePostenFull.map(posten => {
                          const wert = (bwaData[posten.key] || 0) / (zeitraum || 1);
                          return <CostRow key={posten.key} label={posten.label} value={wert} formula="BWA-Wert / Zeitraum"/>
                      })}
                      <div className="flex justify-between items-center py-2 px-3 mt-1 border-t border-pe-border-strong dark:border-slate-600 font-bold text-pe-text text-sm">
                          <span>Summe Gerätekosten</span>
                          <span className="font-mono text-pe-accent-text">{formatCurrency(geraetekostenMonatSumme, 0)}</span>
                      </div>
                  </div>

                 <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2 mt-4">
                       <div className="flex justify-between items-center py-1.5 px-3 font-bold text-pe-text h-10">
                           <span>Gemeinkosten</span>
                           <div className="flex items-center space-x-4">
                               <ValueWithFormula formula="Anteil am Netto-Umsatz"><span className="px-4 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono">{getProzent(gemeinkostenGesamtMonat)}</span></ValueWithFormula>
                               <ValueWithFormula formula="Betriebsk. + unpr. Anteile"><span className="px-4 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono">{formatCurrency(gemeinkostenGesamtMonat, 0)}</span></ValueWithFormula>
                           </div>
                       </div>
                       <div className="flex justify-between items-center py-1.5 px-3 font-bold text-pe-text h-10">
                           <span>Produktive Lohnkosten</span>
                           <div className="flex items-center space-x-4">
                               <ValueWithFormula formula="Anteil am Netto-Umsatz"><span className="px-4 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono">{getProzent(produktiveKostenGesamtMonat)}</span></ValueWithFormula>
                               <ValueWithFormula formula="Summe prod. Löhne (MA + Chef + Azubi 66%)"><span className="px-4 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono">{formatCurrency(produktiveKostenGesamtMonat, 0)}</span></ValueWithFormula>
                           </div>
                       </div>
                       <div className="flex justify-between items-center py-1.5 px-3 font-bold text-pe-text h-10">
                           <span>Materialkosten</span>
                           <div className="flex items-center space-x-4">
                               <ValueWithFormula formula="Anteil am Netto-Umsatz"><span className="px-4 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono">{getProzent(materialkostenMonat)}</span></ValueWithFormula>
                               <ValueWithFormula formula="Skalierter Materialeinsatz"><span className="px-4 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono">{formatCurrency(materialkostenMonat, 0)}</span></ValueWithFormula>
                           </div>
                       </div>
                       <div className="flex justify-between items-center py-1.5 px-3 font-bold text-pe-text h-10">
                           <span>unpr. Unternehmerlohn</span>
                           <div className="flex items-center space-x-4">
                               <ValueWithFormula formula="Anteil am Netto-Umsatz"><span className="px-4 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono">{getProzent(unprodUnternehmerlohn)}</span></ValueWithFormula>
                               <ValueWithFormula formula="Gesamtentnahme - prod. Lohn"><span className="px-4 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono">{formatCurrency(unprodUnternehmerlohn, 0)}</span></ValueWithFormula>
                           </div>
                       </div>
                       <div className="flex justify-between items-center py-1.5 px-3 font-bold text-pe-text h-10">
                           <span>Gerätekosten</span>
                           <div className="flex items-center space-x-4">
                               <ValueWithFormula formula="Anteil am Netto-Umsatz"><span className="px-4 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono">{getProzent(geraetekostenMonat)}</span></ValueWithFormula>
                               <ValueWithFormula formula="Summe aus BWA"><span className="px-4 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-mono">{formatCurrency(geraetekostenMonat, 0)}</span></ValueWithFormula>
                           </div>
                       </div>
                       <div className="flex justify-between items-center py-2 px-3 mt-2 border-t-2 border-pe-border-strong dark:border-slate-600 font-bold text-pe-text text-lg h-12">
                           <span>Gesamtkosten/Sollumsatz</span>
                           <ValueWithFormula formula="Summe aller Kostenarten">
                               <span className="px-4 py-1 rounded text-center min-w-[100px] bg-pe-sunken dark:bg-slate-900 text-pe-accent-text font-mono">{formatCurrency(sollumsatz, 0)}</span>
                           </ValueWithFormula>
                       </div>
                 </div>
                    <SectionHeader title="Zusätzliche Kennwerte" />
                    <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2 mt-4">
                        <div className="flex justify-between items-center py-1.5 px-3 font-bold text-pe-text h-10">
                            <span>Summe Stundenlöhne prod. Team</span>
                            <ValueWithFormula formula="Summe Stundenlöhne (Chef + MA)">
                                 <FormulaDisplay show={showFormulas} formula="" value={summeStundenlohnTeam_details} type="currency" decimalPlaces={2} bgColor="bg-pe-sunken dark:bg-slate-900" />
                            </ValueWithFormula>
                        </div>
                        <div className="flex justify-between items-center py-1.5 px-3 font-bold text-pe-text h-10">
                            <span>Ø Stundenlohn prod. Team</span>
                            <ValueWithFormula formula="(Summe Std.Lohn Prod MA + Ist-Std.Lohn Chef) / (Anz. Prod MA + Chef)">
                                 <FormulaDisplay show={showFormulas} formula="" value={durchschnittlicherStundenlohn_details} type="currency" decimalPlaces={2} bgColor="bg-pe-sunken dark:bg-slate-900" />
                            </ValueWithFormula>
                        </div>
                        <div className="flex justify-between items-center py-1.5 px-3 bg-pe-tile-header dark:bg-slate-800 rounded mb-1 h-10">
                            <span className="text-sm text-pe-muted dark:text-gray-300">Ø tägliche Öffnungszeit</span>
                            <span className="px-3 py-0.5 rounded text-center min-w-[100px] text-pe-accent-text font-semibold font-mono">{durchschnittlicheOeffnungszeit.toFixed(2).replace('.', ',')} Std.</span>
                        </div>
                        <div className="flex justify-between items-center py-1.5 px-3 font-bold text-pe-text h-10">
                            <span>produktiv gerechneter Stundenlohn Azubi/Assistenz ({`${(2/3*100).toFixed(0)}%`})</span>
                            <ValueWithFormula formula={`Summe Std.Lohn Azubi * ${(2/3*100).toFixed(0)}%`}>
                                 <FormulaDisplay show={showFormulas} formula="" value={avgStundenlohnAzubiProd_details} type="currency" decimalPlaces={2} bgColor="bg-pe-sunken dark:bg-slate-900" />
                            </ValueWithFormula>
                        </div>
                        <div className="flex justify-between items-center py-1.5 px-3 font-bold text-pe-text border-t border-pe-border dark:border-slate-700 mt-2 pt-2 bg-pe-tile-header dark:bg-slate-800 rounded mb-1 h-10">
                            <span>Berechnungsstundenlohn (inkl. Chef, prod. MA & prod. Anteil Azubis)</span>
                            <ValueWithFormula formula="Summe Std.Lohn Prod MA + Ist-Std.Lohn Chef + Prod. Std.Lohn Azubi">
                                 <FormulaDisplay show={showFormulas} formula="" value={berechnungsstundenlohnSumme_details} type="currency" decimalPlaces={2} bgColor="bg-pe-sunken dark:bg-slate-900" />
                            </ValueWithFormula>
                        </div>
                        <div className="flex justify-between items-center py-1.5 px-3 font-bold text-pe-text border-t border-pe-border dark:border-slate-700 mt-2 pt-2 text-lg bg-pe-tile-header dark:bg-slate-800 rounded mb-1 h-12">
                            <span>Ø Umsatz pro Tag</span>
                            <ValueWithFormula formula="(Ø Std.Lohn prod. Team * Ø Öffnungszeit) * LF Aktiv">
                                <FormulaDisplay show={showFormulas} formula="" value={durchschnittlicherUmsatzProTag} type="currency" decimalPlaces={0} bgColor="bg-pe-sunken dark:bg-slate-900" />
                            </ValueWithFormula>
                        </div>
                    </div>
            </div>
        </div>
    );
};


// --- Hauptkomponente LohnfaktorPage ---
const LohnfaktorPage = () => {
     const { updateNestedClientData, settings, isHJ1GesperrtGlobal, isHJ2Freigegeben } = useAppStore(state => ({
         updateNestedClientData: state.updateNestedClientData,
         settings: state.settings,
         isHJ1GesperrtGlobal: state.clientData?.isHJ1GesperrtGlobal,
         isHJ2Freigegeben: state.clientData?.isHJ2Freigegeben,
     }));
    const currencySymbol = useAppStore(state => state.settings.countries[state.settings.selectedCountryId]?.currency || 'EUR');
    const hj1Locked = isHj1ClientLocked({ isHJ1GesperrtGlobal, isHJ2Freigegeben });

    const calc = useCalculationsSelector();
    const [showDetailsState, setShowDetailsState] = useState(false);
    const [detailsUnlocked, setDetailsUnlocked] = useState(false);
    const [passwordPromptOpen, setPasswordPromptOpen] = useState(false);
    const [itBzInfoModalOpen, setItBzInfoModalOpen] = useState(false);

    const lohnfaktorDataDefaults = {
         unternehmerStundenlohn_editable: 13.00,
         sicherheitsfaktor: 25.00,
         manualLohnfaktor: 3.50,
         kostendeckungsfaktor1: 2.80,
         kostendeckungsfaktor2: 2.80,
         mindestumsatz1: calc.sollumsatz || 23000,
         mindestumsatz2: calc.sollumsatz || 23000,
    };

    // Fehlende Keys mit Defaults füllen — aber 0 bleibt 0 (kein Überschreiben echter Nullwerte)
    const lohnfaktorData = {
        ...lohnfaktorDataDefaults,
        ...(calc.lohnfaktorData || {}),
        sicherheitsfaktor:
            calc.lohnfaktorData?.sicherheitsfaktor === null ||
            calc.lohnfaktorData?.sicherheitsfaktor === undefined ||
            calc.lohnfaktorData?.sicherheitsfaktor === ''
                ? lohnfaktorDataDefaults.sicherheitsfaktor
                : calc.lohnfaktorData.sicherheitsfaktor,
    };

    const isManualStundenlohnActive = lohnfaktorData.isManualStundenlohnActive === true;
    const isKdfManualOverride = lohnfaktorData.isKdfManualOverride === true;

    // Auto-Modus: konvergierten Soll-Stundenlohn halten (kein Override bei manuellem Modus)
    // GmbH: kein Auto-Write — GF-Schatten-Konvergenz lebt auf Unternehmerwerte (kein personalData-Write-Back)
    useEffect(() => {
        if (!calc.ready || isManualStundenlohnActive) return;
        if (calc.clientData?.unternehmensform === 'GmbH') return;

        const hours = Number(calc.monatsstundenChef) || 0;
        if (!(hours > 0)) return;

        const bedarf = Number(calc.echterBedarfGesamt ?? calc.gesamtlohn) || 0;
        const prodUL = Number(calc.prodUnternehmerlohn) || 0;
        const unprodUL = Number(calc.unprodUnternehmerlohn) || 0;
        const sollumsatzOhneUnternehmerlohn =
            (Number(calc.sollumsatz) || 0) - prodUL - unprodUL;

        const ohneAzubis =
            Number(calc.produktiveLohnkostenMitarbeiterOhneAzubis) ||
            Math.max(
                0,
                (Number(calc.produktiveKostenTabelle) || 0) - prodUL
            );
        const lohnfaktorNennerMitarbeiterOhneAzubis = Math.max(
            0,
            (Number(calc.produktiveKostenTabelle) || 0) - prodUL
        );

        const isSolo =
            isSoloModeActive(calc.clientData) ||
            Number(calc.personalData?.length || 0) === 0;

        const sicherheitsfaktorPercent =
            lohnfaktorData.sicherheitsfaktor === null ||
            lohnfaktorData.sicherheitsfaktor === undefined ||
            lohnfaktorData.sicherheitsfaktor === ''
                ? 25
                : Number(lohnfaktorData.sicherheitsfaktor) || 0;

        const ideal = calculateConvergedUnternehmerStundenlohn({
            isSolo,
            echterBedarfGesamt: bedarf,
            chefMonatsstunden: hours,
            sollumsatzOhneUnternehmerlohn,
            produktiveLohnkostenMitarbeiterOhneAzubis: ohneAzubis,
            lohnfaktorNennerMitarbeiterOhneAzubis,
            kalkulierterUnternehmerUmsatzBrutto:
                Number(calc.kalkulierterUnternehmerUmsatzBrutto) || 0,
            mwstSatz: Number(calc.mwstSatz) || 0,
            sicherheitsfaktorPercent,
            urlaubsausgleichPercent: Number(calc.lohnfortzahlungPercent) || 20,
            initialGuess:
                Number(calc.realisierterStundenlohnChef) ||
                Number(lohnfaktorData.unternehmerStundenlohn_editable) ||
                0,
        });

        const currentSoll = Number(lohnfaktorData.unternehmerStundenlohn_editable) || 0;
        if (Math.abs(currentSoll - ideal) >= 0.005) {
            updateNestedClientData('lohnfaktorData', 'unternehmerStundenlohn_editable', ideal);
        }
    }, [
        calc.ready,
        isManualStundenlohnActive,
        calc.monatsstundenChef,
        calc.echterBedarfGesamt,
        calc.gesamtlohn,
        calc.prodUnternehmerlohn,
        calc.unprodUnternehmerlohn,
        calc.sollumsatz,
        calc.produktiveLohnkostenMitarbeiterOhneAzubis,
        calc.produktiveKostenTabelle,
        calc.kalkulierterUnternehmerUmsatzBrutto,
        calc.mwstSatz,
        calc.lohnfortzahlungPercent,
        calc.realisierterStundenlohnChef,
        calc.personalData,
        calc.clientData,
        lohnfaktorData.sicherheitsfaktor,
        lohnfaktorData.unternehmerStundenlohn_editable,
        updateNestedClientData,
    ]);

    const selectedCountryId = String(settings?.selectedCountryId || '');
    const countryLohnnebenkosten = Number(settings?.country?.Lohnnebenkosten) || 0;
    const countryItBzFaktorVollzeit =
        Number((settings?.country as any)?.ItBz_Faktor_Vollzeit) || 1.8;

    // KDF Live-Automatik: Mindestfaktor so justieren, dass Prognose Lohn === Ziel
    // HJ1 bleibt bei hj1Locked strikt eingefroren; Override stoppt die Automatik.
    useEffect(() => {
        if (!calc.ready || isKdfManualOverride) return;

        const mwstSatzSafe =
            calc.mwstSatz == null || !Number.isFinite(Number(calc.mwstSatz))
                ? 19
                : Number(calc.mwstSatz);
        const mitarbeiterDatenFuerKDF = (calc.personalData || []).filter(
            (ma: EmployeeData) =>
                ma.taetigkeit !== 'produktiver Azubi' && ma.taetigkeit !== 'Azubi'
        );
        const basisUmsatzKostendeckung = mitarbeiterDatenFuerKDF.reduce(
            (sum: number, ma: EmployeeData): number =>
                sum + (ma.grundgehalt || 0) + (ma.lohnzusatz || 0),
            0 as number
        );
        if (!(basisUmsatzKostendeckung > 0)) return;

        const gesamtUnternehmerlohn =
            (Number(calc.prodUnternehmerlohn) || 0) +
            (Number(calc.unprodUnternehmerlohn) || 0);
        const ziel = gesamtUnternehmerlohn;
        if (!(ziel > 0)) return;

        const isItCountry = selectedCountryId.toUpperCase() === 'IT';
        const fallbackLnkRate = isItCountry
            ? (countryItBzFaktorVollzeit - 1) * 100
            : countryLohnnebenkosten;

        const { summeBoni, svKostenAufBoni } = mitarbeiterDatenFuerKDF.reduce(
            (
                acc: { summeBoni: number; svKostenAufBoni: number },
                ma: EmployeeData
            ): { summeBoni: number; svKostenAufBoni: number } => {
                const bonus = ma.bonus || 0;
                acc.summeBoni += bonus;
                const effectiveRate =
                    ma.lohnnebenkostenSatz !== undefined && ma.lohnnebenkostenSatz !== null
                        ? ma.lohnnebenkostenSatz
                        : fallbackLnkRate;
                acc.svKostenAufBoni += bonus * (effectiveRate / 100);
                return acc;
            },
            { summeBoni: 0, svKostenAufBoni: 0 }
        );
        const gesamtkostenBoni = summeBoni + svKostenAufBoni;
        const gesamtzielBereinigt =
            (Number(calc.sollumsatz) || 0) - gesamtUnternehmerlohn - gesamtkostenBoni;
        const unternehmerUmsatzBrutto =
            Number(
                calc.kalkulierterUnternehmerUmsatzBrutto ?? calc.erwarteterBruttoUmsatz
            ) || 0;

        // Exakte Einpassung: Prognose(ohne ceil) === Ziel
        // (= Fixpunkt der Proportionalformel; vermeidet Write-Loops durch Math.ceil)
        const mwstFaktor = 1 + mwstSatzSafe / 100;
        const kdfExact =
            ((ziel + gesamtzielBereinigt) * mwstFaktor - unternehmerUmsatzBrutto) /
            basisUmsatzKostendeckung;
        if (!Number.isFinite(kdfExact)) return;
        const next = Number(Math.max(2.8, kdfExact).toFixed(2));

        const maybeWriteKdf = (key: 'kostendeckungsfaktor1' | 'kostendeckungsfaktor2', currentRaw: any) => {
            const current = Number(Math.max(2.8, Number(currentRaw) || 2.8).toFixed(2));
            if (current === next) return;
            updateNestedClientData('lohnfaktorData', key, next);
        };

        // HJ1: nur wenn NICHT gesperrt
        if (!hj1Locked) {
            maybeWriteKdf('kostendeckungsfaktor1', lohnfaktorData.kostendeckungsfaktor1);
        }
        // HJ2: Automatik immer (sofern kein Manual-Override)
        maybeWriteKdf('kostendeckungsfaktor2', lohnfaktorData.kostendeckungsfaktor2);
    }, [
        calc.ready,
        isKdfManualOverride,
        hj1Locked,
        calc.personalData,
        calc.prodUnternehmerlohn,
        calc.unprodUnternehmerlohn,
        calc.sollumsatz,
        calc.mwstSatz,
        calc.kalkulierterUnternehmerUmsatzBrutto,
        calc.erwarteterBruttoUmsatz,
        lohnfaktorData.kostendeckungsfaktor1,
        lohnfaktorData.kostendeckungsfaktor2,
        selectedCountryId,
        countryLohnnebenkosten,
        countryItBzFaktorVollzeit,
        updateNestedClientData,
    ]);

    if (!calc.ready) {
         return <Page><div className="text-pe-text p-8 text-center">Lade Lohnfaktor-Daten...</div></Page>;
    }

    const handleLohnfaktorDataChange = (key: string, value: any) => {
         // HJ1-Felder hart gesperrt nach Freigabe (auch Gott-Modus)
         if (hj1Locked && (key === 'mindestumsatz1' || key === 'kostendeckungsfaktor1')) {
             return;
         }
         let numValue = value;
         
         if (key === 'kostendeckungsfaktor1' || key === 'kostendeckungsfaktor2') {
              numValue = Math.max(2.80, numValue);
              // Manuelle KDF-Eingabe friert die Live-Automatik ein
              if (lohnfaktorData.isKdfManualOverride !== true) {
                  updateNestedClientData('lohnfaktorData', 'isKdfManualOverride', true);
              }
         }
         updateNestedClientData('lohnfaktorData', key, numValue);
    };

    const handleKdfResetToAuto = () => {
         updateNestedClientData('lohnfaktorData', 'isKdfManualOverride', false);
    };

     const handleShowDetailsToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
         const isChecked = e.target.checked;
         if (isChecked && !detailsUnlocked) {
             setPasswordPromptOpen(true);
              e.preventDefault();
         } else {
             setShowDetailsState(isChecked);
             if (!isChecked) {
                 setDetailsUnlocked(false);
             }
         }
    };

    const handlePasswordConfirm = (password: string) => {
         if (password === '90') {
             setDetailsUnlocked(true);
             setShowDetailsState(true);
             setPasswordPromptOpen(false);
         } else {
             alert('Falsches Passwort.');
             setPasswordPromptOpen(false);
              setShowDetailsState(false);
         }
    };

    const { formatCurrency } = calc;
    // Polster/Planumsatz: Lohnfaktor aktiv (mind. 3,50 bzw. Manual, wenn höher)
    const planumsatzKalkuliert = calc.planumsatzGesamtNettoKalkuliert ?? calc.planumsatzGesamtNetto;
    const differenzPolster = calc.differenzKalkuliert ?? (planumsatzKalkuliert - calc.kalkSollumsatzInklFortzahlung);
    const liquiditaetsaufschlagProzent = settings.global.Liquiditaet || 10;
    const mwstSatzSafe = (calc.mwstSatz == null || !Number.isFinite(Number(calc.mwstSatz))) ? 19 : Number(calc.mwstSatz);
    
    // Liquiditätsaufschlag für Monatsumsätze (rechts)
    const monatsumsatzNettoLiquiditaet = lohnfaktorData.mindestumsatz2 * (1 + liquiditaetsaufschlagProzent / 100);
    const monatsumsatzBruttoLiquiditaet = monatsumsatzNettoLiquiditaet * (1 + mwstSatzSafe / 100);
    const monatszielBrutto = lohnfaktorData.mindestumsatz2 * (1 + mwstSatzSafe / 100);

     const mitarbeiterDatenFuerKDF = (calc.personalData || []).filter((ma: EmployeeData) => ma.taetigkeit !== 'produktiver Azubi' && ma.taetigkeit !== 'Azubi');
     const basisUmsatzKostendeckung = mitarbeiterDatenFuerKDF.reduce((sum: number, ma: EmployeeData): number => sum + (ma.grundgehalt || 0) + (ma.lohnzusatz || 0), 0 as number);
     const kostendeckungsfaktor1 = lohnfaktorData.kostendeckungsfaktor1;
     const kostendeckungsfaktor2 = lohnfaktorData.kostendeckungsfaktor2;
     const gesamtUnternehmerlohn = calc.prodUnternehmerlohn + calc.unprodUnternehmerlohn;
     const { summeBoni, svKostenAufBoni } = mitarbeiterDatenFuerKDF.reduce((acc: { summeBoni: number, svKostenAufBoni: number }, ma: EmployeeData): { summeBoni: number, svKostenAufBoni: number } => {
         const bonus = ma.bonus || 0;
         acc.summeBoni += bonus;
         const isItCountry = String(settings?.selectedCountryId || '').toUpperCase() === 'IT';
         const fallbackLnkRate = isItCountry
             ? ((Number((settings?.country as any)?.ItBz_Faktor_Vollzeit) || 1.80) - 1) * 100
             : (settings.country.Lohnnebenkosten || 0);
         const effectiveRate = ma.lohnnebenkostenSatz !== undefined && ma.lohnnebenkostenSatz !== null
             ? ma.lohnnebenkostenSatz
             : fallbackLnkRate;
         acc.svKostenAufBoni += bonus * (effectiveRate / 100);
         return acc;
     }, { summeBoni: 0, svKostenAufBoni: 0 });

     const gesamtkostenBoni = summeBoni + svKostenAufBoni;
     const gesamtzielBereinigt = calc.sollumsatz - gesamtUnternehmerlohn - gesamtkostenBoni;
     // GbR-Weiche über Selektor: kalkulierterUnternehmerUmsatzBrutto = Partner-Summe bei aktiv, sonst Einzel — KEIN Direktzugriff auf unternehmerData
     const unternehmerUmsatzBrutto = Number(calc.kalkulierterUnternehmerUmsatzBrutto ?? calc.erwarteterBruttoUmsatz) || 0;

     const kostendeckungsumsatzBrutto1 = basisUmsatzKostendeckung * kostendeckungsfaktor1;
     const gesamtumsatzTeamBrutto1 = kostendeckungsumsatzBrutto1 + unternehmerUmsatzBrutto;
     const gesamtumsatzTeamNetto1 = gesamtumsatzTeamBrutto1 / (1 + (mwstSatzSafe / 100));
     const differenzKDF1 = gesamtumsatzTeamNetto1 - gesamtzielBereinigt;
     const prognoseLohn1 = Math.ceil(differenzKDF1);

     const kostendeckungsumsatzBrutto2 = basisUmsatzKostendeckung * kostendeckungsfaktor2;
     const gesamtumsatzTeamBrutto2 = kostendeckungsumsatzBrutto2 + unternehmerUmsatzBrutto;
     const gesamtumsatzTeamNetto2 = gesamtumsatzTeamBrutto2 / (1 + (mwstSatzSafe / 100));
     const differenzKDF2 = gesamtumsatzTeamNetto2 - gesamtzielBereinigt;
     const prognoseLohn2 = Math.ceil(differenzKDF2);

     const mitarbeiterDatenFuerPlanumsatz = (calc.personalData || []).filter((ma: EmployeeData) => ma.taetigkeit !== 'produktiver Azubi' && ma.taetigkeit !== 'Azubi');
     const summeStundenlohnProdMA = mitarbeiterDatenFuerPlanumsatz.reduce((sum: number, ma: EmployeeData): number => sum + (ma.stundenlohnGesamt || 0), 0 as number);
     // Excel: Summe(Azubi-Std-Löhne) * 2/3 — nicht Ø * 2/3
     const avgStundenlohnAzubiProd = calc.azubiAssistenzData.length > 0
         ? (calc.azubiAssistenzData as EmployeeData[]).reduce((sum: number, ma: EmployeeData) => sum + (ma.stundenlohnGesamt || 0), 0 as number) * (2 / 3)
         : 0;
     const anzahlMitarbeiterProd = mitarbeiterDatenFuerPlanumsatz.length;
     const chefWochenstunden = calc.prodWochenstundenChef ?? calc.clientData?.unternehmerData?.prodWochenstunden ?? 0;
     const anzahlChefProd = chefWochenstunden > 0 ? 1 : 0;
     // Excel: volle Azubi-Kopfzahl im Nenner (nicht pauschal 1)
     const anzahlAzubiFaktor = calc.azubiAssistenzData.length;
     const anzahlProduktivesTeamGesamt = anzahlMitarbeiterProd + anzahlChefProd + anzahlAzubiFaktor;
     
     const isSoloStatus = calc.clientData?.isSolo === true || calc.clientData?.companyProfile?.isSolo === true || calc.isSolo === true || anzahlMitarbeiterProd === 0;
     const chefMonatsstunden = calc.monatsstundenChef ?? (chefWochenstunden * 4.33);
     
     const erforderlicherSoloStundenlohn = chefMonatsstunden > 0 
         ? gesamtUnternehmerlohn / chefMonatsstunden 
         : 0;
     
     const safeSummeStundenlohnProdMA = Number(summeStundenlohnProdMA) || 0;
     const safeUnternehmerStundenlohn = Number(lohnfaktorData.unternehmerStundenlohn_editable) || 0;
     const safeAvgStundenlohnAzubiProd = Number(avgStundenlohnAzubiProd) || 0;

     const teamStundenlohn = anzahlProduktivesTeamGesamt > 0 ? (summeStundenlohnProdMA + (lohnfaktorData.unternehmerStundenlohn_editable || 0) + avgStundenlohnAzubiProd) / anzahlProduktivesTeamGesamt : 0;
     const teamStundenlohnFormel = `(${safeSummeStundenlohnProdMA.toFixed(2)}${currencySymbol} + ${safeUnternehmerStundenlohn.toFixed(2)}${currencySymbol} + ${safeAvgStundenlohnAzubiProd.toFixed(2)}${currencySymbol}) / ${anzahlProduktivesTeamGesamt}`;

     // --- CHIRURGIE-FIX: EXCEL-GEGENRECHNUNG JETZT STRICT AUF MONATSBASIS ---
     const ziel1Monat = lohnfaktorData.mindestumsatz1 * (1 + liquiditaetsaufschlagProzent / 100);
     const ziel2Monat = lohnfaktorData.mindestumsatz2 * (1 + liquiditaetsaufschlagProzent / 100);
     const istMonat = planumsatzKalkuliert;
     
     const diff1 = istMonat - ziel1Monat;
     const diff2 = istMonat - ziel2Monat;

    return (
        <Page>
            <PasswordPrompt
                isOpen={passwordPromptOpen}
                onClose={() => setPasswordPromptOpen(false)}
                onConfirm={handlePasswordConfirm}
            />
            <div className="flex flex-col items-center space-y-8">
                <Section className="max-w-5xl relative">
                        <div className="absolute top-4 right-4 z-10">
                             <div className="bg-pe-tile-header dark:bg-slate-700 p-2 rounded-lg shadow-lg border border-slate-500">
                                 <label className="flex items-center space-x-2 text-pe-text cursor-pointer">
                                     <input
                                      type="checkbox"
                                      checked={showDetailsState && detailsUnlocked}
                                      onChange={handleShowDetailsToggle}
                                      className="form-checkbox h-5 w-5 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                                     />
                                     <span className="font-semibold text-sm">Details anzeigen</span>
                                 </label>
                             </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <PageTitle>Lohnfaktor</PageTitle>
                            <button
                                type="button"
                                onClick={() => setItBzInfoModalOpen(true)}
                                title="Lohnberechnung & Lohnfaktor Südtirol (IT_BZ) — Systematik & Nachweis"
                                className="mb-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-[#dcb68a]/50 bg-[#15171e] hover:bg-[#dcb68a] text-[#dcb68a] hover:text-black text-[11px] font-black uppercase tracking-wider transition-all shadow-sm"
                            >
                                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full border border-current text-[10px] leading-none">i</span>
                                <span>IT_BZ Systematik</span>
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4 pt-4">
                            {/* Linke Spalte */}
                            <div className="space-y-3">
                                <DataBox label="Lohnfaktor netto" tooltip="Wird aus dem kalkulatorischen Sollumsatz und den gesamten produktiven Lohnkosten berechnet.">
                                    <ValueWithFormula formula="kalk. Sollumsatz inkl. Lohnfortz. / (prod. Lohnk. MA + prod. Lohnk. Chef)">
                                                <EditableField value={calc.lohnfaktorNetto || 0} readOnly type="number" decimalPlaces={2} />
                                    </ValueWithFormula>
                                 </DataBox>
                                 <DataBox label="Lohnfaktor brutto" tooltip="Netto-Lohnfaktor inklusive der gesetzlichen Mehrwertsteuer.">
                                    <ValueWithFormula formula="Lohnfaktor Netto * (1 + MwSt%)">
                                                <EditableField value={calc.lohnfaktorBrutto || 0} readOnly type="number" decimalPlaces={2} />
                                    </ValueWithFormula>
                                 </DataBox>
                                <div className="flex items-center space-x-2 h-auto">
                                     <div className="flex-grow space-y-3">
                                         <div className="flex items-center space-x-2 h-11">
                                             <div className="w-1/2 bg-pe-tile-header dark:bg-gray-900 text-pe-text text-sm font-semibold p-2 rounded-l-md border-r border-pe-border dark:border-slate-700 flex items-center h-full">
                                                 Lohnfaktor + Fortzahlung
                                             </div>
                                             <div className="w-1/2 h-full relative">
                                                 <ValueWithFormula formula="Lohnfaktor Brutto * (1 + Sicherheitsfaktor %)">
                                                      <EditableField 
                                                        value={calc.lohnfaktorFortzahlung || 0} 
                                                        readOnly 
                                                        type="number" 
                                                        decimalPlaces={2} 
                                                        className="w-full h-full bg-pe-input dark:bg-slate-800 text-pe-accent-text font-bold text-center"
                                                      />
                                                 </ValueWithFormula>
                                             </div>
                                         </div>

                                         <div className="flex items-center space-x-2 h-11">
                                             <div className="w-1/2 bg-pe-tile-header dark:bg-gray-900 text-pe-text text-sm font-semibold p-2 rounded-l-md border-r border-pe-border dark:border-slate-700 flex items-center h-full">
                                                 Lohnfaktor aktiv
                                             </div>
                                             <div className="w-1/2 h-full relative">
                                                 <ValueWithFormula formula="= max(3,50; LF+Fortzahlung; Manual) — für Leistungsseiten">
                                                      <EditableField 
                                                        value={calc.lohnfaktorAktiv || 0} 
                                                        readOnly 
                                                        type="number" 
                                                        decimalPlaces={2} 
                                                        className="w-full h-full font-bold text-center"
                                                        style={{ backgroundColor: '#15803d', color: 'white' }}
                                                      />
                                                 </ValueWithFormula>
                                             </div>
                                         </div>
                                     </div>
                                     <DisplayCard label="Sicherheitsfaktor" containerClassName="w-32 flex-shrink-0 self-end">
                                         <EditableField 
                                            value={lohnfaktorData.sicherheitsfaktor || 0} 
                                            onChange={(val: any) => handleLohnfaktorDataChange('sicherheitsfaktor', val)} 
                                            type="percentage"
                                            decimalPlaces={2}
                                            className="text-center font-bold"
                                         />
                                     </DisplayCard>
                                 </div>
                                <hr className="border-pe-border dark:border-slate-700 my-4" />
                                
                                <DataBox label="Sollumsatz (netto)" tooltip="Die reinen Kosten, die gedeckt werden müssen (ohne Puffer).">
                                     <ValueWithFormula formula="Sachkosten + Materialk. + ges. Lohnkosten">
                                          <EditableField value={calc.sollumsatz || 0} readOnly type="currency" currency={currencySymbol} decimalPlaces={0} />
                                     </ValueWithFormula>
                                 </DataBox>

                                <div className="space-y-3 pt-2">
                                    <div className="flex items-center space-x-2 h-11">
                                         <div className="w-1/2 bg-pe-tile-header dark:bg-gray-900 text-pe-text text-sm font-semibold p-2 rounded-l-md border-r border-pe-border dark:border-slate-700 flex items-center h-full">
                                             kalk. Sollumsatz + Lohnfortz. (netto)
                                         </div>
                                         <div className="w-1/2 h-full relative">
                                            <ValueWithFormula formula={`Sollumsatz + (prod. Lohnk. MA * ${calc.settings.global.Urlaubsausgleich}%)`}>
                                                <EditableField 
                                                    value={calc.kalkSollumsatzInklFortzahlung || 0} 
                                                    readOnly 
                                                    type="currency" 
                                                    currency={currencySymbol} 
                                                    decimalPlaces={0} 
                                                    className="w-full h-full bg-pe-input dark:bg-slate-800 text-pe-accent-text font-bold text-center"
                                                />
                                            </ValueWithFormula>
                                         </div>
                                    </div>

                                    <div className="flex items-center space-x-2 h-11">
                                         <div className="w-1/2 bg-pe-tile-header dark:bg-gray-900 text-pe-text text-sm font-semibold p-2 rounded-l-md border-r border-pe-border dark:border-slate-700 flex items-center h-full">
                                             Planumsatz gesamt (netto)
                                         </div>
                                         <div className="w-1/2 h-full relative">
                                            <ValueWithFormula formula="(Basis-Bruttolöhne MA inkl. Bonus + prod. UL) * Lohnfaktor aktiv / (1+MwSt%)">
                                                <EditableField 
                                                    value={planumsatzKalkuliert || 0} 
                                                    readOnly 
                                                    type="currency" 
                                                    currency={currencySymbol} 
                                                    decimalPlaces={0} 
                                                    className="w-full h-full font-bold text-center"
                                                    style={{ backgroundColor: '#15803d', color: 'white' }}
                                                />
                                            </ValueWithFormula>
                                         </div>
                                    </div>
                                </div>

                                 <hr className="border-pe-border dark:border-slate-700 my-4" />
                                 <DataBox label="Jahresumsatz (netto)" tooltip="Gesamter Jahresumsatz (netto) aus BWA und Prognosen.">
                                     <ValueWithFormula formula="(BWA Umsatz + aktive Prognosen) / Zeitraum * 12">
                                              <EditableField value={(calc.gesamtUmsatz / (calc.zeitraum || 1) * 12) || 0 } readOnly type="currency" currency={currencySymbol} decimalPlaces={0} />
                                     </ValueWithFormula>
                                 </DataBox>
                                 
                                 <div className="mt-4 p-4 bg-pe-tile-header dark:bg-gray-900 rounded-lg border border-pe-border dark:border-slate-700">
                                     <Hj1MandantLockBanner show={hj1Locked} />
                                     <div className="grid grid-cols-2 gap-4">
                                          <div className="flex-1">
                                              <DisplayCard label="1. Halbjahr (netto)" containerClassName="w-full">
                                                  <EditableField 
                                                      value={lohnfaktorData.mindestumsatz1 || 0} 
                                                      onChange={(val: any) => handleLohnfaktorDataChange('mindestumsatz1', val)}
                                                      type="currency" 
                                                      currency={currencySymbol}
                                                      decimalPlaces={0} 
                                                      className="text-center font-bold text-lg w-full"
                                                      readOnly={hj1Locked}
                                                  />
                                              </DisplayCard>
                                          </div>
                                          <div className="flex-1">
                                              <DisplayCard label="2. Halbjahr (netto)" containerClassName="w-full">
                                                  <EditableField 
                                                      value={lohnfaktorData.mindestumsatz2 || 0} 
                                                      onChange={(val: any) => handleLohnfaktorDataChange('mindestumsatz2', val)}
                                                      type="currency" 
                                                      currency={currencySymbol}
                                                      decimalPlaces={0} 
                                                      className="text-center font-bold text-lg w-full"
                                                  />
                                              </DisplayCard>
                                          </div>
                                     </div>
                                      <div className="pt-3 text-center">
                                         <ValueWithFormula formula="Sachkosten + Materialk. + ges. Lohnkosten">
                                             <div className="text-base font-medium text-pe-muted dark:text-gray-400">
                                                 Sollumsatz p. Monat netto: <span className="text-pe-text font-bold">{formatCurrency(calc.sollumsatz, 0).replace('€', currencySymbol).replace('CHF', currencySymbol)}</span>
                                             </div>
                                         </ValueWithFormula>
                                     </div>
                                 </div>

                            </div>
                            {/* Mittlere Spalte */}
                            <div className="space-y-3 flex flex-col items-center w-full">
                                 <DisplayCard label="Lohnfaktor Manuell">
                                    <EditableField
                                        value={lohnfaktorData.manualLohnfaktor || 0}
                                        onChange={(val: any) => handleLohnfaktorDataChange('manualLohnfaktor', val)}
                                        type="number"
                                        decimalPlaces={2}
                                        className="text-center font-bold"
                                    />
                                    <p className="text-[9px] text-slate-500 mt-1 text-center leading-tight max-w-[140px]">
                                        Wenn Manual &gt; LF+Fortzahlung: aktiv &amp; Planumsatz steigen mit. IST-Stundenlohn bleibt bei LF+Fortzahlung.
                                    </p>
                                 </DisplayCard>

                                 <div className="h-11"></div>
                                 <div className="h-11 mb-3"></div>
                                 
                                 <div className="flex-shrink-0 flex flex-col justify-start items-center">
                                     <DisplayCard label="Polster" containerClassName="w-32">
                                         <ValueWithFormula formula="Planumsatz (aktiv) − kalk. Sollumsatz inkl. Fortz.">
                                             <EditableField 
                                                 value={differenzPolster || 0} 
                                                 readOnly 
                                                 type="currency" 
                                                 currency={currencySymbol}
                                                 decimalPlaces={0}
                                                 className="text-center font-bold border"
                                                 style={{ 
                                                     backgroundColor: differenzPolster >= 1000 ? '#15803d' : (differenzPolster >= 0 ? '#854d0e' : '#7f1d1d'),
                                                     color: 'white',
                                                     borderColor: differenzPolster >= 1000 ? '#166534' : (differenzPolster >= 0 ? '#ca8a04' : '#991b1b')
                                                 }}
                                             />
                                         </ValueWithFormula>
                                     </DisplayCard>
                                     <p className="text-xs text-pe-muted dark:text-gray-400 pt-1 text-center max-w-[120px]">Ziel: mind. 1.000 {currencySymbol} Polster</p>
                                 </div>

                                 {/* CHIRURGIE-FIX: SAUBERES & KOMPAKTES CSS-GRID FÜR EXCEL-GEGENRECHNUNG */}
                                 {(!isSoloStatus && summeBoni > 0) && (
                                     <div className="w-full max-w-[340px] mx-auto mt-4 bg-pe-tile-header dark:bg-slate-800/80 border border-pe-border dark:border-slate-700 rounded p-2.5 shadow-md font-sans">
                                          <div className="w-full flex justify-end pb-1 mb-1.5 border-b border-pe-border dark:border-slate-700/50">
                                               <span className="text-slate-500 text-[9px] uppercase tracking-wider">Werte pro Monat (netto)</span>
                                          </div>

                                          <div className="grid grid-cols-[auto_auto_85px_1fr] gap-x-2 gap-y-1 items-center">
                                              {/* Row 1: Ziel H1 */}
                                              <div className="col-span-2"></div>
                                              <div className="bg-pe-sunken dark:bg-slate-900 border border-pe-border-strong dark:border-slate-600 rounded px-1.5 py-0.5 text-right text-pe-muted dark:text-gray-300 font-mono text-xs">
                                                  {formatCurrency(ziel1Monat, 0)}
                                              </div>
                                              <div className="text-pe-muted dark:text-gray-400 text-[10px] whitespace-nowrap">Ziel 1. Hj (+{liquiditaetsaufschlagProzent}%)</div>

                                              {/* Row 2: Ist H1 */}
                                              <div className="text-right text-gray-500 text-[10px]">Diff.</div>
                                              <div className={`text-right font-mono font-semibold text-xs whitespace-nowrap ${diff1 >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                  {diff1 >= 0 ? '+' : ''}{formatCurrency(diff1, 0)}
                                              </div>
                                              <div className="bg-pe-sunken dark:bg-slate-900 border border-pe-border-strong dark:border-slate-600 rounded px-1.5 py-0.5 text-right text-pe-text font-mono font-bold text-xs shadow-inner">
                                                  {formatCurrency(istMonat, 0)}
                                              </div>
                                              <div className="text-pe-muted dark:text-gray-400 text-[10px] whitespace-nowrap">Ist 1. Hj</div>

                                              {/* Divider */}
                                              <div className="col-span-4 border-b border-pe-border dark:border-slate-700/50 my-1.5"></div>

                                              {/* Row 3: Ziel H2 */}
                                              <div className="col-span-2"></div>
                                              <div className="bg-pe-sunken dark:bg-slate-900 border border-pe-border-strong dark:border-slate-600 rounded px-1.5 py-0.5 text-right text-pe-muted dark:text-gray-300 font-mono text-xs">
                                                  {formatCurrency(ziel2Monat, 0)}
                                              </div>
                                              <div className="text-pe-muted dark:text-gray-400 text-[10px] whitespace-nowrap">Ziel 2. Hj (+{liquiditaetsaufschlagProzent}%)</div>

                                              {/* Row 4: Ist H2 */}
                                              <div className="text-right text-gray-500 text-[10px]">Diff.</div>
                                              <div className={`text-right font-mono font-semibold text-xs whitespace-nowrap ${diff2 >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                  {diff2 >= 0 ? '+' : ''}{formatCurrency(diff2, 0)}
                                              </div>
                                              <div className="bg-pe-sunken dark:bg-slate-900 border border-pe-border-strong dark:border-slate-600 rounded px-1.5 py-0.5 text-right text-pe-text font-mono font-bold text-xs shadow-inner">
                                                  {formatCurrency(istMonat, 0)}
                                              </div>
                                              <div className="text-pe-muted dark:text-gray-400 text-[10px] whitespace-nowrap">Ist 2. Hj</div>
                                          </div>
                                     </div>
                                 )}

                            </div>
                            {/* Rechte Spalte */}
                            <div className="space-y-3">
                                 
                                 {isSoloStatus && (
                                     <div className="bg-pink-900/40 border border-pink-500 rounded-md p-2 flex items-center justify-between shadow-lg shadow-pink-900/20">
                                         <div className="flex flex-col text-left">
                                             <span className="text-pink-300 text-[10px] font-bold uppercase tracking-wider">Soloselbstständigkeit</span>
                                             <span className="text-pe-text text-xs">
                                                 {Number(calc.unprodUnternehmerlohn || 0) === 0
                                                     ? 'Kein unproduktiver Unternehmerlohn — 100 % produktiv gerechnet.'
                                                     : 'Bitte Soll-Wert anpassen ➔'}
                                             </span>
                                         </div>
                                         <ValueWithFormula formula="Gesamtunternehmerlohn / Chef Monatsstunden">
                                             <div className="bg-pink-600 text-white font-mono font-bold text-lg px-3 py-1 rounded">
                                                 {formatCurrency(erforderlicherSoloStundenlohn, 2)}
                                             </div>
                                         </ValueWithFormula>
                                     </div>
                                 )}

                                 <DisplayCard
                                     label="Unternehmerstundenlohn"
                                     containerClassName="w-full"
                                     headerExtra={
                                         <label className="inline-flex items-center gap-1.5 text-[10px] font-normal text-pe-muted dark:text-gray-400 cursor-pointer select-none tracking-normal normal-case">
                                             <input
                                                 type="checkbox"
                                                 className="rounded border-pe-border dark:border-slate-600"
                                                 checked={isManualStundenlohnActive}
                                                 onChange={(e) =>
                                                     handleLohnfaktorDataChange(
                                                         'isManualStundenlohnActive',
                                                         e.target.checked
                                                     )
                                                 }
                                             />
                                             Manuell justieren
                                         </label>
                                     }
                                 >
                                     <div className="flex items-center justify-center space-x-2 mt-1">
                                         <div className={`flex-1 text-center ${isManualStundenlohnActive ? 'text-[#F8FAFC]' : 'text-[#E6C299]'}`} data-tooltip-id="app-tooltip" data-tooltip-content={isManualStundenlohnActive ? 'Manueller Soll-Stundenlohn.' : 'Auto: konvergierter Soll-Stundenlohn (gesperrt).'}>
                                              <div className="text-[10px] text-pe-muted dark:text-gray-400 mb-1 uppercase tracking-wider">
                                                  Soll-Wert {isManualStundenlohnActive ? '(editierbar)' : '(Auto / gesperrt)'}
                                              </div>
                                              <EditableField
                                                  value={lohnfaktorData.unternehmerStundenlohn_editable || 0}
                                                  onChange={(val: any) => handleLohnfaktorDataChange('unternehmerStundenlohn_editable', val)}
                                                  readOnly={!isManualStundenlohnActive}
                                                  transparent
                                                  type="currency"
                                                  currency={currencySymbol}
                                                  decimalPlaces={2}
                                                  className={`text-center font-bold border border-pe-border dark:border-white/10 rounded-md ${
                                                      isManualStundenlohnActive
                                                          ? 'bg-white dark:bg-[#0a0a0a] text-[#F8FAFC]'
                                                          : 'bg-stone-100 dark:bg-[#0a0a0a] text-[#E6C299]'
                                                  }`}
                                              />
                                         </div>
                                         <div className="flex-1 text-center border-l border-pe-border dark:border-slate-700 pl-2">
                                              <div className="text-[10px] text-pe-muted dark:text-gray-400 mb-1 uppercase tracking-wider">Ist-Wert (errechnet)</div>
                                              <ValueWithFormula formula="(Ø Monatsumsatz Chef / LF+Fortzahlung) / Monatsstunden — ohne Manual-Floor">
                                                    <EditableField 
                                                        value={calc.realisierterStundenlohnChef || 0} 
                                                        readOnly 
                                                        type="currency" 
                                                        currency={currencySymbol} 
                                                        decimalPlaces={2} 
                                                        className="text-center text-pe-muted dark:text-gray-300" 
                                                    />
                                              </ValueWithFormula>
                                         </div>
                                     </div>
                                 </DisplayCard>
                                <hr className="border-pe-border dark:border-slate-700 my-4" />
                                <DataBox label="Chef Wochenstunden" tooltip="Summe der produktiven Wochenstunden aller Unternehmer (bei GbR: Partner addiert).">
                                     <EditableField value={calc.prodWochenstundenChef ?? calc.clientData?.unternehmerData?.prodWochenstunden ?? 0} readOnly suffix="Std." className="text-center" />
                                 </DataBox>
                                 <DataBox label="prod. Unternehmerlohn" tooltip="Der Anteil deines Lohns, der durch produktive Arbeit erwirtschaftet wird.">
                                     <ValueWithFormula formula="Std.Lohn Soll * Monatsstunden Chef">
                                          <EditableField value={calc.prodUnternehmerlohn || 0} readOnly type="currency" currency={currencySymbol} decimalPlaces={0} />
                                     </ValueWithFormula>
                                 </DataBox>
                                 <DataBox label="unprod. Unternehmerlohn" tooltip="Der Anteil deines Lohns, der durch unproduktive Tätigkeiten gedeckt werden muss.">
                                     <ValueWithFormula formula="Gesamtentnahme - prod. Lohn">
                                          <EditableField value={calc.unprodUnternehmerlohn || 0} readOnly type="currency" currency={currencySymbol} decimalPlaces={0} />
                                     </ValueWithFormula>
                                 </DataBox>
                                 <hr className="border-pe-border dark:border-slate-700 my-4" />
                                 <div className="pt-4 flex justify-end">
                                      <div className="flex flex-col space-y-3 items-end">
                                           <DataBox label="Liquiditätsaufschlag +" tooltip="Prozentualer Aufschlag auf den Mindestumsatz zur Sicherung der Liquidität. Kommt aus Globale Parameter.">
                                                <EditableField value={liquiditaetsaufschlagProzent || 0} readOnly type="percentage" decimalPlaces={0} className="text-center" />
                                           </DataBox>
                                          <DataBox label="Teamstundenlohn Ø" tooltip="Durchschnittlicher Stundenlohn aller produktiven Kräfte (Chef + MA + Azubi-Anteil)">
                                               <ValueWithFormula formula={teamStundenlohnFormel}>
                                                    <EditableField value={teamStundenlohn || 0} readOnly type="currency" currency={currencySymbol} decimalPlaces={2}/>
                                               </ValueWithFormula>
                                          </DataBox>
                                          <div className="pt-4 w-full">
                                               <HeaderBox title="Monatsumsätze inklusive Liquidität" />
                                               <div className="flex space-x-2">
                                                  <DisplayCard label={`Netto +${liquiditaetsaufschlagProzent}%`}>
                                                        <ValueWithFormula formula="Mindestumsatz 2. Hj * (1 + Liquiditätsaufschlag %)">
                                                             <EditableField value={monatsumsatzNettoLiquiditaet || 0} readOnly type="currency" currency={currencySymbol} decimalPlaces={0} />
                                                        </ValueWithFormula>
                                                  </DisplayCard>
                                                  <DisplayCard label={`Brutto +${liquiditaetsaufschlagProzent}%`}>
                                                        <ValueWithFormula formula="Monatsumsatz Netto (Liq.) * (1 + MwSt%)">
                                                             <EditableField value={monatsumsatzBruttoLiquiditaet || 0} readOnly type="currency" currency={currencySymbol} decimalPlaces={0} />
                                                        </ValueWithFormula>
                                                  </DisplayCard>
                                               </div>
                                          </div>
                                          
                                          <div className="pt-4 w-full">
                                               <ValueWithFormula formula="Mindestumsatz 2. Hj * (1 + MwSt.)">
                                                   <DisplayCard label="Monatsziel (brutto)" containerClassName="w-full border-blue-500/50">
                                                       <EditableField value={monatszielBrutto || 0} readOnly type="currency" currency={currencySymbol} decimalPlaces={0} className="text-center font-bold text-lg w-full" />
                                                   </DisplayCard>
                                               </ValueWithFormula>
                                          </div>
                                      </div>
                                 </div>
                            </div>
                        </div>
                    </Section>

                     <Section className="w-full max-w-5xl">
                         <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                             <PageTitle>Kostendeckungsfaktor Analyse</PageTitle>
                             <div className="flex items-center gap-3 flex-wrap justify-end">
                                 <label className="inline-flex items-center gap-1.5 text-[10px] font-normal text-pe-muted dark:text-gray-400 cursor-pointer select-none tracking-normal normal-case">
                                     <input
                                         type="checkbox"
                                         className="rounded border-pe-border dark:border-slate-600"
                                         checked={isKdfManualOverride}
                                         onChange={(e) =>
                                             updateNestedClientData(
                                                 'lohnfaktorData',
                                                 'isKdfManualOverride',
                                                 e.target.checked
                                             )
                                         }
                                     />
                                     Manuell justieren
                                 </label>
                                 {isKdfManualOverride && (
                                     <button
                                         type="button"
                                         onClick={handleKdfResetToAuto}
                                         className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded border border-[#E6C299]/40 text-[#E6C299] hover:bg-[#E6C299]/10 transition-colors"
                                         title="Live-Automatik wieder aktivieren (Prognose Lohn = Ziel)"
                                     >
                                         Reset → Auto
                                     </button>
                                 )}
                                 <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                                     {isKdfManualOverride
                                         ? 'Override aktiv'
                                         : hj1Locked
                                           ? 'Auto (nur HJ2)'
                                           : 'Auto (HJ1+HJ2)'}
                                 </span>
                             </div>
                         </div>
                         <div className="bg-pe-tile-header dark:bg-gray-900 mt-4 p-4 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 border border-pe-border dark:border-slate-700">
                               <div className="space-y-3 p-3 bg-gray-800 rounded-lg border border-pe-border dark:border-slate-700 flex flex-col">
                                   <div className="text-lg text-pe-text mb-2 font-semibold text-center">1. Halbjahr</div>
                                   <DisplayCard label="Mindestfaktor" containerClassName="w-full">
                                           <EditableField
                                                value={kostendeckungsfaktor1 || 0}
                                                onChange={(val: any) => handleLohnfaktorDataChange('kostendeckungsfaktor1', val)}
                                                type="number"
                                                decimalPlaces={2}
                                                className="text-center font-bold"
                                                readOnly={hj1Locked || !isKdfManualOverride}
                                           />
                                    </DisplayCard>
                                   <DisplayCard label="Prognose Lohn" containerClassName="w-full">
                                                 <div className="bg-pe-tile-header dark:bg-slate-700 border border-pe-border-strong dark:border-slate-600 rounded-md h-full flex items-center justify-center p-1">
                                                     <EditableField 
                                                        value={prognoseLohn1 || 0} 
                                                        readOnly 
                                                        type="currency" 
                                                        currency={currencySymbol} 
                                                        decimalPlaces={0} 
                                                        className="w-full h-full font-bold border-none text-center"
                                                        style={{ backgroundColor: 'transparent', color: '#22c55e' }}
                                                    />
                                                 </div>
                                    </DisplayCard>
                                    <DisplayCard label="Ziel" containerClassName="w-full">
                                                  <div className="bg-pe-tile-header dark:bg-slate-700 border border-pe-border-strong dark:border-slate-600 rounded-md h-full flex items-center justify-center p-1">
                                                      <EditableField 
                                                          value={gesamtUnternehmerlohn || 0} 
                                                          readOnly 
                                                          type="currency" 
                                                          currency={currencySymbol} 
                                                          decimalPlaces={0} 
                                                          className="w-full h-full border-none text-center"
                                                          style={{ backgroundColor: 'transparent', color: '#E6C299' }}
                                                      />
                                                  </div>
                                    </DisplayCard>
                               </div>
                                <div className="space-y-3 p-3 bg-gray-800 rounded-lg border border-pe-border dark:border-slate-700 flex flex-col">
                                     <div className="text-lg text-pe-text mb-2 font-semibold text-center">2. Halbjahr</div>
                                     <DisplayCard label="Mindestfaktor" containerClassName="w-full">
                                          <EditableField
                                                value={kostendeckungsfaktor2 || 0}
                                                onChange={(val: any) => handleLohnfaktorDataChange('kostendeckungsfaktor2', val)}
                                                type="number"
                                                decimalPlaces={2}
                                                className="text-center font-bold"
                                                readOnly={!isKdfManualOverride}
                                          />
                                     </DisplayCard>
                                     <DisplayCard label="Prognose Lohn" containerClassName="w-full">
                                                  <div className="bg-pe-tile-header dark:bg-slate-700 border border-pe-border-strong dark:border-slate-600 rounded-md h-full flex items-center justify-center p-1">
                                                      <EditableField 
                                                          value={prognoseLohn2 || 0} 
                                                          readOnly 
                                                          type="currency" 
                                                          currency={currencySymbol} 
                                                          decimalPlaces={0} 
                                                          className="w-full h-full font-bold border-none text-center"
                                                          style={{ backgroundColor: 'transparent', color: '#22c55e' }}
                                                       />
                                                  </div>
                                     </DisplayCard>
                                     <DisplayCard label="Ziel" containerClassName="w-full">
                                                  <div className="bg-pe-tile-header dark:bg-slate-700 border border-pe-border-strong dark:border-slate-600 rounded-md h-full flex items-center justify-center p-1">
                                                      <EditableField 
                                                          value={gesamtUnternehmerlohn || 0} 
                                                          readOnly 
                                                          type="currency" 
                                                          currency={currencySymbol} 
                                                          decimalPlaces={0} 
                                                          className="w-full h-full border-none text-center"
                                                          style={{ backgroundColor: 'transparent', color: '#E6C299' }}
                                                      />
                                                  </div>
                                     </DisplayCard>
                               </div>
                         </div>
                      </Section>

                {showDetailsState && detailsUnlocked && (
                     <div className="w-full max-w-5xl flex-shrink-0 mt-8">
                         <DetailsAnsicht calc={calc} />
                     </div>
                )}
            </div>
            <ItBzLohnberechnungInfoModal
                isOpen={itBzInfoModalOpen}
                onClose={() => setItBzInfoModalOpen(false)}
            />
        </Page>
    );
};
export default LohnfaktorPage;